import React, { useEffect, useRef } from "react";
import type { OnboardingAnswers, SelfLevel } from "../../../types/onboarding";
import { levelOptions } from "../../../data/onboarding";
import { OptionCard } from "../OptionCard";
import { StepHeading } from "../StepHeading";
import { LangChip } from "../../ui/LangChip";
import { getLanguage } from "../../../utils/language";
interface LevelStepProps {
  answers: OnboardingAnswers;
  update: (p: Partial<OnboardingAnswers>) => void;
  onDone: () => void;
  targetName: string;
}
const shortLabels = ["New", "A few words", "Getting by", "Confident"];
function Bars({
  value
}: {
  value: number;
}) {
  return <span className="flex h-6 items-end gap-0.5" aria-hidden="true" data-id="element-991" data-mp="2kl5r4gh79al">
      {[0, 1, 2, 3].map(i => <span key={i} className={`w-1.5 rounded-sm ${i <= value ? "bg-brand" : "bg-sunken"}`} style={{
      height: `${35 + i * 22}%`
    }} data-id="element-992" data-mp="3ts5z195nqo0n" />)}
    </span>;
}
export function LevelStep({
  answers,
  update,
  onDone,
  targetName
}: LevelStepProps) {
  const timer = useRef<number>();
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const others = answers.targets.slice(1);
  const pick = (value: SelfLevel) => {
    // Changing the self-report invalidates any earlier checks.
    const same = value === answers.selfLevel;
    update({
      selfLevel: value,
      canDo: same ? answers.canDo : [],
      quiz: same ? answers.quiz : []
    });
    // Only auto-advance when there's nothing else to answer on this screen.
    if (others.length === 0) {
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(onDone, 260);
    }
  };
  return <div data-id="element-993" data-mp="389xtlnay7icc">
      <StepHeading title={`How much ${targetName} do you know?`} description="Your honest first guess. If you know some already, we'll confirm it with a few taps — no one starts in the wrong place." data-id="element-994" data-mp="38b903uvs3475" />
      <ul className="grid gap-3" role="radiogroup" aria-label={`Your ${targetName} level`} data-id="element-995" data-mp="3hcp8jx318w8q">
        {levelOptions.map(l => <li key={l.value} data-id="element-996" data-mp="1m7pkhf4a2n8v">
            <OptionCard role="radio" selected={answers.selfLevel === l.value} onClick={() => pick(l.value)} title={l.label} description={l.hint} aside={<Bars value={l.value} data-id="element-998" data-mp="1vyao5gyt6hyn" />} data-id="element-997" data-mp="1m6y0l1k1mdyf" />
          </li>)}
      </ul>

      {others.length > 0 && <section aria-labelledby="other-levels" className="mt-9" data-id="element-999" data-mp="1r5himqvin5li">
          <h2 id="other-levels" className="text-sm font-bold text-ink" data-id="element-1000" data-mp="24x5z16cssg8a">
            And your other languages?
          </h2>
          <ul className="mt-3 space-y-3" data-id="element-1001" data-mp="20wc9k8jpitda">
            {others.map(code => {
          const value = answers.otherLevels[code] ?? 0;
          return <li key={code} className="flex flex-col gap-2.5 rounded-card border-3 border-line/30 bg-surface p-3 sm:flex-row sm:items-center" data-id="element-1002" data-mp="29ybrd32jq1ha">
                  <span className="flex min-w-[130px] items-center gap-2" data-id="element-1003" data-mp="kkggnoe24iuj">
                    <LangChip code={code} size="sm" tone="tint" data-id="element-1004" data-mp="fku29bmhswk5" />
                    <span className="text-sm font-semibold text-ink" data-id="element-1005" data-mp="2r6cn8oxcfs9v">{getLanguage(code).name}</span>
                  </span>
                  <div className="grid flex-1 grid-cols-2 gap-1.5 sm:grid-cols-4" role="radiogroup" aria-label={`${getLanguage(code).name} level`} data-id="element-1006" data-mp="2w4e91hm5m5p1">
                    {shortLabels.map((label, i) => {
                const active = value === i;
                return <button key={label} type="button" role="radio" aria-checked={active} onClick={() => update({
                  otherLevels: {
                    ...answers.otherLevels,
                    [code]: i as SelfLevel
                  }
                })} className={`focus-ring whitespace-nowrap rounded-lg px-2 py-2 text-xs font-semibold transition-colors duration-150 ${active ? "bg-ink text-paper" : "bg-sunken text-ink-2 hover:text-ink"}`} data-id="element-1007" data-mp="28f3ckcbu6xgp" data-mpi={i}>
                          {label}
                        </button>;
              })}
                  </div>
                </li>;
        })}
          </ul>
        </section>}
    </div>;
}